import React, { useState, useRef } from 'react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import { format, parse, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-day-picker/style.css';

const DAY_PICKER_CLASS_NAMES = getDefaultClassNames();

export interface CustomDatePickerProps {
    /** Valor no formato dd/mm/yyyy */
    value?: string;
    /** Callback disparado sempre que o valor mudar */
    onChange: (value: string) => void;
    onBlur?: () => void;
    /** Placeholder exibido quando vazio */
    placeholder?: string;
    /** Desabilita o campo */
    disabled?: boolean;
    /** Id opcional para acessibilidade */
    id?: string;
    /** Data-testid opcional para testes */
    ['data-testid']?: string;
    /** Classe extra */
    className?: string;
    isDateSelectable?: (date: Date) => boolean;
    defaultMonth?: Date;
    highlightDate?: (date: Date) => boolean;
    hideTodayHighlight?: boolean;
}

/**
 * Componente de data com input manual e date picker.
 * Usa react-day-picker que é muito consolidada e compatível com React 19.
 * Permite tanto digitação manual quanto seleção via date picker.
 * Formato padrão: dd/mm/yyyy
 */
const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
    value = '',
    onChange,
    onBlur,
    placeholder = 'dd/mm/aaaa',
    disabled = false,
    id,
    className = '',
    isDateSelectable,
    defaultMonth,
    highlightDate,
    hideTodayHighlight = false,
    ...rest
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value);
    const [hasRejectedInput, setHasRejectedInput] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const onBlurRef = useRef(onBlur);
    onBlurRef.current = onBlur;

    // Sincronizar valor interno com prop externa
    React.useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Converter string para Date
    const stringToDate = (dateString: string): Date | undefined => {
        if (!dateString) return undefined;
        const parsed = parse(dateString, 'dd/MM/yyyy', new Date());
        return isValid(parsed) ? parsed : undefined;
    };

    // Converter Date para string
    const dateToString = (date: Date | undefined): string => {
        if (!date) return '';
        return format(date, 'dd/MM/yyyy');
    };

    // Handler para mudanças no input manual
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let digitsOnly = e.target.value.replace(/\D/g, '');

        // Limita a 8 dígitos (ddMMyyyy)
        digitsOnly = digitsOnly.slice(0, 8);

        let masked = digitsOnly;

        if (digitsOnly.length > 2) {
            masked = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
        }

        if (digitsOnly.length > 4) {
            masked = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
        }

        setInputValue(masked);

        if (digitsOnly.length === 0) {
            setHasRejectedInput(false);
            onChange('');
        } else if (digitsOnly.length === 8) {
            const parsed = stringToDate(masked);
            const rejected = isDateSelectable ? !parsed || !isDateSelectable(parsed) : false;
            setHasRejectedInput(rejected);
            onChange(rejected ? '' : masked);
        } else {
            setHasRejectedInput(false);
        }
    };

    // Handler para seleção no date picker
    const handleDaySelect = (date: Date | undefined) => {
        const dateString = dateToString(date);
        setInputValue(dateString);
        setHasRejectedInput(false);
        onChange(dateString);
        onBlur?.();
        setIsOpen(false);
    };

    // Toggle do date picker
    const toggleDatePicker = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    // Fechar ao clicar fora
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                onBlurRef.current?.();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const selectedDate = stringToDate(inputValue);

    return (
        <div className={`relative ${className} mt-1`} {...rest}>
            {/* Input manual */}
            <div className="relative">
                <input
                    id={id}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-invalid={hasRejectedInput || undefined}
                    className={`input-primary w-full pr-12 ${
                        hasRejectedInput
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                            : ''
                    }`}
                    maxLength={10} // dd/mm/yyyy = 10 caracteres
                    data-testid={rest['data-testid']}
                />

                {/* Ícone do calendário */}
                <button
                    type="button"
                    onClick={toggleDatePicker}
                    disabled={disabled}
                    className="absolute inset-y-0 right-0 flex items-center px-3 cursor-pointer disabled:cursor-not-allowed rounded-r-md"
                >
                    <svg
                        className="w-5 h-5 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                        />
                    </svg>
                </button>
            </div>

            {/* Date Picker Popover */}
            {isOpen && (
                <div
                    ref={popoverRef}
                    className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-md shadow-lg z-50 p-3"
                >
                    <DayPicker
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDaySelect}
                        disabled={isDateSelectable ? (date) => !isDateSelectable(date) : undefined}
                        defaultMonth={selectedDate ?? defaultMonth}
                        locale={ptBR}
                        showOutsideDays
                        modifiers={highlightDate ? { highlighted: highlightDate } : undefined}
                        modifiersClassNames={
                            highlightDate
                                ? { highlighted: 'bg-primary-100 text-primary-700 font-semibold rounded-full' }
                                : undefined
                        }
                        classNames={{
                            day_button: `${DAY_PICKER_CLASS_NAMES.day_button} hover:bg-primary-50 rounded-full transition-colors`,
                            disabled: `${DAY_PICKER_CLASS_NAMES.disabled} !opacity-100 text-gray-300`,
                            ...(hideTodayHighlight ? { today: '' } : {}),
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default CustomDatePicker; 
